import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, unref as _unref, createVNode as _createVNode, createTextVNode as _createTextVNode } from "vue"

const _hoisted_1 = { class: "space-y-3" }
const _hoisted_2 = { class: "flex items-center justify-between mb-2" }
const _hoisted_3 = { class: "text-xs font-semibold text-gray-500 uppercase tracking-wide" }
const _hoisted_4 = { class: "flex items-center gap-0.5" }
const _hoisted_5 = ["disabled", "onClick"]
const _hoisted_6 = ["disabled", "onClick"]
const _hoisted_7 = ["onClick"]
const _hoisted_8 = { class: "mb-2" }
const _hoisted_9 = { class: "flex flex-wrap items-center gap-1.5" }
const _hoisted_10 = ["aria-label", "onClick"]
const _hoisted_11 = ["onChange"]
const _hoisted_12 = ["value"]
const _hoisted_13 = { class: "grid grid-cols-2 gap-2" }
const _hoisted_14 = ["value", "onChange"]
const _hoisted_15 = ["value"]
const _hoisted_16 = ["value", "onChange"]
const _hoisted_17 = ["value"]

import { ArrowDown, ArrowUp, Plus, Trash2, X } from 'lucide-vue-next'
import type { RegleBrouillon } from '@/types/workflowTemplate'

interface RoleOption { id: number; code: string; nom: string }
interface DirectionOption { id: number; code: string; nom: string }
interface WorkflowOption { id: number; nom: string; is_active: boolean }


export default /*@__PURE__*/_defineComponent({
  __name: 'DocumentTypeWorkflowRulesEditor',
  props: {
    modelValue: {},
    roles: {},
    directions: {},
    workflowTemplates: {}
  },
  emits: ["update:modelValue"],
  setup(__props: any, { emit: __emit }) {

const props = __props

const emit = __emit

const publier = (regles: RegleBrouillon[]) => emit('update:modelValue', regles)

const modifier = (idx: number, champs: Partial<RegleBrouillon>) => {
  publier(props.modelValue.map((r, i) => (i === idx ? { ...r, ...champs } : r)))
}

const ajouter = () => {
  publier([...props.modelValue, { role_ids: [], direction_id: '', workflow_template_id: '' }])
}

const retirer = (idx: number) => {
  publier(props.modelValue.filter((_, i) => i !== idx))
}

const deplacer = (idx: number, sens: -1 | 1) => {
  const cible = idx + sens
  if (cible < 0 || cible >= props.modelValue.length) return
  const regles = [...props.modelValue]
  ;[regles[idx], regles[cible]] = [regles[cible], regles[idx]]
  publier(regles)
}

const ajouterRole = (idx: number, event: Event) => {
  const select = event.target as HTMLSelectElement
  const roleId = Number(select.value)
  select.value = ''
  if (!roleId) return
  const regle = props.modelValue[idx]
  if (regle.role_ids.includes(roleId)) return
  modifier(idx, { role_ids: [...regle.role_ids, roleId] })
}

const retirerRole = (idx: number, roleId: number) => {
  modifier(idx, { role_ids: props.modelValue[idx].role_ids.filter(id => id !== roleId) })
}

const nomDuRole = (roleId: number) => props.roles.find(r => r.id === roleId)?.nom ?? `Rôle #${roleId}`

const rolesDisponibles = (regle: RegleBrouillon) => props.roles.filter(r => !regle.role_ids.includes(r.id))

// Workflows actifs, plus celui déjà choisi s'il a été désactivé depuis (pour ne pas
// le faire disparaître du formulaire sans prévenir).
const workflowsProposes = (regle: RegleBrouillon) =>
  props.workflowTemplates.filter(wt => wt.is_active || String(wt.id) === regle.workflow_template_id)

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", null, [
    _cache[7] || (_cache[7] = _createElementVNode("label", { class: "block text-sm font-medium text-[#22282F] mb-1" }, "Circuits selon le profil du demandeur", -1)),
    _cache[8] || (_cache[8] = _createElementVNode("p", { class: "text-xs text-gray-400 mb-3" }, " Les règles se lisent dans l'ordre : la première qui correspond au rédacteur choisit le workflow. Si aucune ne correspond, c'est le workflow de validation ci-dessus. ", -1)),
    _createElementVNode("div", _hoisted_1, [
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_ctx.modelValue, (regle, idx) => {
        return (_openBlock(), _createElementBlock("div", {
          key: idx,
          class: "border border-gray-200 rounded-lg p-3 bg-gray-50/60",
          "data-test": "regle"
        }, [
          _createElementVNode("div", _hoisted_2, [
            _createElementVNode("span", _hoisted_3, "Règle " + _toDisplayString(idx + 1), 1),
            _createElementVNode("div", _hoisted_4, [
              _createElementVNode("button", {
                type: "button",
                disabled: idx === 0,
                class: "p-1 text-gray-500 hover:text-gray-900 disabled:opacity-30",
                title: "Monter",
                onClick: ($event: any) => (deplacer(idx, -1))
              }, [
                _createVNode(_unref(ArrowUp), { class: "w-4 h-4" })
              ], 8, _hoisted_5),
              _createElementVNode("button", {
                type: "button",
                disabled: idx === _ctx.modelValue.length - 1,
                class: "p-1 text-gray-500 hover:text-gray-900 disabled:opacity-30",
                title: "Descendre",
                onClick: ($event: any) => (deplacer(idx, 1))
              }, [
                _createVNode(_unref(ArrowDown), { class: "w-4 h-4" })
              ], 8, _hoisted_6),
              _createElementVNode("button", {
                type: "button",
                class: "p-1 text-red-500 hover:text-red-600",
                title: "Retirer la règle",
                "data-test": "retirer-regle",
                onClick: ($event: any) => (retirer(idx))
              }, [
                _createVNode(_unref(Trash2), { class: "w-4 h-4" })
              ], 8, _hoisted_7)
            ])
          ]),
          _createElementVNode("div", _hoisted_8, [
            _cache[1] || (_cache[1] = _createElementVNode("span", { class: "block text-xs text-gray-500 mb-1" }, "Rôles", -1)),
            _createElementVNode("div", _hoisted_9, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(regle.role_ids, (roleId) => {
                return (_openBlock(), _createElementBlock("span", {
                  key: roleId,
                  class: "inline-flex items-center gap-1 bg-white border border-gray-200 text-gray-700 text-xs rounded-md pl-2 pr-1 py-0.5",
                  "data-test": "role"
                }, [
                  _createTextVNode(_toDisplayString(nomDuRole(roleId)) + " ", 1),
                  _createElementVNode("button", {
                    type: "button",
                    class: "p-0.5 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-700",
                    "aria-label": `Retirer ${nomDuRole(roleId)}`,
                    onClick: ($event: any) => (retirerRole(idx, roleId))
                  }, [
                    _createVNode(_unref(X), { class: "w-3 h-3" })
                  ], 8, _hoisted_10)
                ]))
              }), 128)),
              _createElementVNode("select", {
                class: "px-2 py-1 border border-gray-200 rounded-md text-xs bg-white focus:outline-none focus:border-[#22282F]",
                "data-test": "ajout-role",
                onChange: ($event: any) => (ajouterRole(idx, $event))
              }, [
                _cache[0] || (_cache[0] = _createElementVNode("option", { value: "" }, "+ Ajouter un rôle", -1)),
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(rolesDisponibles(regle), (role) => {
                  return (_openBlock(), _createElementBlock("option", {
                    key: role.id,
                    value: String(role.id)
                  }, _toDisplayString(role.nom) + " (" + _toDisplayString(role.code) + ") ", 9, _hoisted_12))
                }), 128))
              ], 40, _hoisted_11)
            ])
          ]),
          _createElementVNode("div", _hoisted_13, [
            _createElementVNode("div", null, [
              _cache[3] || (_cache[3] = _createElementVNode("span", { class: "block text-xs text-gray-500 mb-1" }, "Direction", -1)),
              _createElementVNode("select", {
                value: regle.direction_id,
                class: "w-full px-2 py-1.5 border border-gray-200 rounded-md text-xs bg-white focus:outline-none focus:border-[#22282F]",
                "data-test": "direction",
                onChange: ($event: any) => (modifier(idx, { direction_id: ($event.target as HTMLSelectElement).value }))
              }, [
                _cache[2] || (_cache[2] = _createElementVNode("option", { value: "" }, "Toutes les directions", -1)),
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_ctx.directions, (d) => {
                  return (_openBlock(), _createElementBlock("option", {
                    key: d.id,
                    value: String(d.id)
                  }, _toDisplayString(d.code) + " — " + _toDisplayString(d.nom), 9, _hoisted_15))
                }), 128))
              ], 40, _hoisted_14)
            ]),
            _createElementVNode("div", null, [
              _cache[5] || (_cache[5] = _createElementVNode("span", { class: "block text-xs text-gray-500 mb-1" }, "Workflow", -1)),
              _createElementVNode("select", {
                value: regle.workflow_template_id,
                class: "w-full px-2 py-1.5 border border-gray-200 rounded-md text-xs bg-white focus:outline-none focus:border-[#22282F]",
                "data-test": "workflow",
                onChange: ($event: any) => (modifier(idx, { workflow_template_id: ($event.target as HTMLSelectElement).value }))
              }, [
                _cache[4] || (_cache[4] = _createElementVNode("option", { value: "" }, "-- Choisir un workflow --", -1)),
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(workflowsProposes(regle), (wt) => {
                  return (_openBlock(), _createElementBlock("option", {
                    key: wt.id,
                    value: String(wt.id)
                  }, _toDisplayString(wt.nom) + _toDisplayString(wt.is_active ? '' : ' (désactivé)'), 9, _hoisted_17))
                }), 128))
              ], 40, _hoisted_16)
            ])
          ])
        ]))
      }), 128))
    ]),
    _createElementVNode("button", {
      type: "button",
      class: "mt-3 inline-flex items-center gap-1.5 text-sm text-[#22282F] hover:underline",
      "data-test": "ajouter-regle",
      onClick: ajouter
    }, [
      _createVNode(_unref(Plus), { class: "w-4 h-4" }),
      _cache[6] || (_cache[6] = _createTextVNode(" Ajouter une règle "))
    ])
  ]))
}
}

})